import { useState } from 'react';

export default function Hero() {
  const [copied, setCopied] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText('RAMESCO');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleScroll = () => {
    if (window.scrollY > 50) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  };

  useState(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#0A1628] via-[#0D1B3A] to-[#1a0b2e]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0A1628]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img 
            src="https://public.readdy.ai/ai/img_res/bcb95e27-fe0e-4195-af23-4243a6196df3.png" 
            alt="QT Funded Logo" 
            className="h-12 w-auto"
          />
          <a 
            href="https://qtfunded.quanttekel.com/ref/5428/?campaign=ramesco"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Get Started
          </a>
        </div>
      </nav>

      {/* Background Effects */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500 rounded-full filter blur-[120px]"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-600 rounded-full filter blur-[120px]"></div>
      </div>

      {/* Animated Grid Pattern */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }}></div>

      {/* Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 flex items-center min-h-screen">
        <div className="grid lg:grid-cols-2 gap-12 items-center w-full">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-block px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 rounded-full">
              <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">Limited Time Offer</span>
            </div>

            <h1 className="text-6xl lg:text-7xl font-black text-white leading-tight">
              UNLOCK <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">55% OFF</span><br />
              YOUR TRADING<br />
              FUTURE TODAY
            </h1>

            <p className="text-xl text-gray-300 leading-relaxed">
              Exclusive RAMESCO discount code - Start your funded trading journey with QT Funded at an unbeatable price
            </p>

            {/* Promo Code Card */}
            <div className="relative p-6 bg-gradient-to-r from-cyan-500/10 to-purple-600/10 border-2 border-cyan-500/30 rounded-2xl backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 mb-2">Your Exclusive Code</p>
                  <p className="text-4xl font-mono font-bold text-white tracking-wider">RAMESCO</p>
                </div>
                <button
                  onClick={handleCopy}
                  className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  {copied ? (
                    <>
                      <i className="ri-check-line text-xl"></i>
                      Copied!
                    </>
                  ) : (
                    <>
                      <i className="ri-file-copy-line text-xl"></i>
                      Copy Code
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://qtfunded.quanttekel.com/ref/5428/?campaign=ramesco"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Claim 55% Discount Now
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
              <button
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-transparent border-2 border-white/30 text-white text-lg font-semibold rounded-full hover:bg-white/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Learn How It Works
              </button>
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Floating Metric Cards */}
              <div className="absolute top-0 right-0 p-6 bg-gradient-to-br from-cyan-500/20 to-transparent border border-cyan-500/30 rounded-2xl backdrop-blur-md animate-float">
                <p className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">55% OFF</p>
                <p className="text-sm text-gray-300 mt-2">Exclusive Discount</p>
              </div>

              <div className="absolute top-40 left-0 p-6 bg-gradient-to-br from-purple-600/20 to-transparent border border-purple-500/30 rounded-2xl backdrop-blur-md animate-float-delayed">
                <p className="text-4xl font-black text-white">$200K</p>
                <p className="text-sm text-gray-300 mt-2">Max Funding</p>
              </div>

              <div className="absolute bottom-20 right-10 p-6 bg-gradient-to-br from-blue-500/20 to-transparent border border-blue-500/30 rounded-2xl backdrop-blur-md animate-float">
                <p className="text-4xl font-black text-white">No Limits</p>
                <p className="text-sm text-gray-300 mt-2">Time Freedom</p>
              </div>

              {/* Chart Visualization */}
              <div className="relative w-full h-96 flex items-end justify-around gap-2 px-8">
                {[65, 45, 80, 55, 90, 70, 85, 60, 95, 75].map((height, index) => (
                  <div
                    key={index}
                    className="w-full bg-gradient-to-t from-cyan-500/50 to-blue-500/50 rounded-t-lg animate-chart-grow"
                    style={{
                      height: `${height}%`,
                      animationDelay: `${index * 0.1}s`
                    }}
                  ></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-3xl text-white/50"></i>
      </div>
    </section>
  );
}
